var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useCallback, useState } from 'react';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { selectTool } from '../helper/helper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { playAudio, stopAudio } from '../helper/audioHelper';
import { announce, announceAssertive } from '../helper/announcer';
import { useCreationAnnounce, useDuplicateAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import ButtonComponent from '../components/ButtonComponent';
import globalStore from '../store';
import '../styles/widgets/numberRackManipulative.scss';
import { t } from 'i18next';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
var BEAD_DIAMETER = 39.4;
var BEAD_SPACING = 39.4;
var ROD_LEFT_PADDING = 3.4;
var ROD_WIDTH = 873.6; // 10 left beads + 2 bead center gap + 10 right beads + 2×padding
var ROW_GAP = BEAD_DIAMETER * 1.5; // 59.1px — gap between rows
// true = loop drag sound while bead moves, false = play once on first movement
var CONTINUOUS_DRAG_SOUND = true;
export var DEFAULT_BEAD_POSITIONS = [
    476.2, 515.6, 555.0, 594.4, 633.8, 673.2, 712.6, 752.0, 791.4, 830.8,
];
export function makeDefaultRod(index) {
    var isReversed = index >= 5;
    return {
        id: "rod_".concat(index),
        type: 'rod',
        data: {
            beads: [0, 0, 0, 0, 0, 1, 1, 1, 1, 1],
            beadsRawPosition: __spreadArray([], DEFAULT_BEAD_POSITIONS, true),
            isReversed: isReversed,
        },
    };
}
/**
 * Build a detailed screen-reader announcement describing a bead movement.
 * Compares old vs new positions, groups by color (red / white) and direction
 * (left / right), then renders a human-readable sentence plus a row-state
 * suffix so a blind user can understand exactly what happened.
 *
 * Returns an empty string if nothing moved.
 */
var buildBeadMoveAnnouncement = function (oldPositions, newPositions, beads, isReversed) {
    var redLeft = 0, redRight = 0, whiteLeft = 0, whiteRight = 0;
    var TOL = 0.5;
    for (var i = 0; i < oldPositions.length; i++) {
        var delta = newPositions[i] - oldPositions[i];
        if (Math.abs(delta) <= TOL)
            continue;
        var isRed = isReversed ? beads[i] === 1 : beads[i] === 0;
        if (delta < 0) {
            if (isRed)
                redLeft++;
            else
                whiteLeft++;
        }
        else {
            if (isRed)
                redRight++;
            else
                whiteRight++;
        }
    }
    var anyMoved = redLeft + redRight + whiteLeft + whiteRight > 0;
    if (!anyMoved)
        return '';
    var phraseFor = function (red, white) {
        if (red > 0 && white > 0) {
            if (red === 1 && white === 1)
                return t('text.beadMovedMixedBothOne');
            if (red === 1)
                return t('text.beadMovedMixedOneRed', { white: white });
            if (white === 1)
                return t('text.beadMovedMixedOneWhite', { red: red });
            return t('text.beadMovedMixed', { red: red, white: white });
        }
        if (red > 0) {
            return red === 1
                ? t('text.beadMovedSingle', { count: red, color: t('text.red') })
                : t('text.beadMovedPlural', { count: red, color: t('text.red') });
        }
        return white === 1
            ? t('text.beadMovedSingle', { count: white, color: t('text.white') })
            : t('text.beadMovedPlural', { count: white, color: t('text.white') });
    };
    var leftTotal = redLeft + whiteLeft;
    var rightTotal = redRight + whiteRight;
    var body;
    if (leftTotal > 0 && rightTotal > 0) {
        body = t('text.beadMovedBothDirections', {
            leftPhrase: t('text.beadMovedDirection', {
                phrase: phraseFor(redLeft, whiteLeft),
                direction: t('text.left'),
            }),
            rightPhrase: t('text.beadMovedDirection', {
                phrase: phraseFor(redRight, whiteRight),
                direction: t('text.right'),
            }),
        });
    }
    else if (leftTotal > 0) {
        body = t('text.beadMovedDirection', {
            phrase: phraseFor(redLeft, whiteLeft),
            direction: t('text.left'),
        });
    }
    else {
        body = t('text.beadMovedDirection', {
            phrase: phraseFor(redRight, whiteRight),
            direction: t('text.right'),
        });
    }
    return "".concat(body, ".");
};
var NumberRackManipulative = function (_a) {
    var _b, _c;
    var data = _a.data;
    var id = data.id, rods = data.rods, center = data.center, dimensions = data.dimensions, scale = data.scale, lock = data.lock;
    var _d = useContext(DataContext), selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, setManipulative = _d.setManipulative, manipulative = _d.manipulative, panningEnable = _d.panningEnable, setContextMenu = _d.setContextMenu;
    var creationLabel = useCreationAnnounce(id, t('text.numberRack'));
    var duplicateLabel = useDuplicateAnnounce(id, t('text.numberRack'));
    // Focus the wrapper when this rack was just duplicated so the screen reader
    // reads the duplicate announcement and the user lands on the new copy.
    useEffect(function () {
        if (duplicateLabel) {
            requestAnimationFrame(function () {
                var _a;
                (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Reliable touch-primary detection — works on modern iPads (desktop UA)
    var isTouchPrimary = typeof window !== 'undefined' && !!((_b = window.matchMedia) === null || _b === void 0 ? void 0 : _b.call(window, '(pointer: coarse)').matches);
    // Track whether focus came from a screen reader (VoiceOver sets :focus-visible on focus)
    var lastFocusWasScreenReader = useRef(false);
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var zoomLevel = require('../store').default.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _e = useState(false), isDragging = _e[0], setIsDragging = _e[1];
    var _f = useState(null), focusedBead = _f[0], setFocusedBead = _f[1];
    var _g = useState(null), selectedRow = _g[0], setSelectedRow = _g[1];
    var wrapperRef = useRef(null);
    var beadRefs = useRef(new Map());
    var positionDragRef = useRef(null);
    var dragStartPositionRef = useRef(null);
    var currentPositionRef = useRef(null);
    var beadDragRef = useRef(null);
    var rodsRef = useRef(rods);
    rodsRef.current = rods;
    var scrapingRef = useRef(false);
    var scrapingTimeoutRef = useRef(null);
    var lastBeadXRef = useRef(null);
    // Tracks which adjacent bead pairs (by left-bead index) are currently touching
    var touchingPairsRef = useRef(new Set());
    // Stop-timer for the keyboard / Shift Left-Right slide audio so its duration
    // scales with bead-widths actually moved instead of always playing the full
    // audio file. Mouse drag uses scrapingTimeoutRef instead and is untouched.
    var nudgeAudioStopTimerRef = useRef(null);
    var isFocused = selectedTool.includes(id);
    var getClientPoint = function (e) {
        if ('touches' in e) {
            var touch = e.touches[0] || e.changedTouches[0];
            return { x: touch.clientX, y: touch.clientY };
        }
        return { x: e.clientX, y: e.clientY };
    };
    // ─── Bottom Panel + Right Panel Event Handling ───
    useEffect(function () {
        var onToolClick = function (btnData) {
            switch (btnData.id) {
                case 'scaleMinus':
                case 'scalePlus': {
                    var isPlus = btnData.id === 'scalePlus';
                    var oldScale = scale !== null && scale !== void 0 ? scale : 1;
                    var newScale_1 = isPlus ? oldScale + 0.1 : Math.max(0.5, oldScale - 0.1);
                    if (newScale_1 === oldScale)
                        break;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(oldScale),
                        new: JSON.stringify(newScale_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.id === id ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { scale: newScale_1 }) }) : _man;
                        });
                    });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.numberRack'),
                        direction: isPlus ? t('text.up') : t('text.down'),
                        value: newScale_1.toFixed(1),
                    }));
                    break;
                }
                case 'addRow': {
                    if (rods.length >= 10)
                        break;
                    var oldRods = JSON.stringify(rods);
                    var newRowIndex = rods.length;
                    // Rows at index 5+ default to reversed; match existing 6+ rows if any
                    var newIsReversed = false;
                    if (newRowIndex >= 5) {
                        var existingReversedRow = rods.find(function (_, idx) { return idx >= 5; });
                        newIsReversed = existingReversedRow ? existingReversedRow.data.isReversed : true;
                    }
                    var newRod = {
                        id: "rod_".concat(Date.now()),
                        type: 'rod',
                        data: {
                            beads: [0, 0, 0, 0, 0, 1, 1, 1, 1, 1],
                            beadsRawPosition: __spreadArray([], DEFAULT_BEAD_POSITIONS, true),
                            isReversed: newIsReversed,
                        },
                    };
                    var newRods_1 = __spreadArray(__spreadArray([], rods, true), [newRod], false);
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'rods',
                        old: oldRods,
                        new: JSON.stringify(newRods_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.id === id ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { rods: newRods_1 }) }) : _man;
                        });
                    });
                    announce(t('text.numberRackRowAdded', { count: newRods_1.length }) +
                        (newIsReversed
                            ? " ".concat(t('text.numberRackNewRowWhiteFirst', { n: newRowIndex + 1 }))
                            : ''));
                    break;
                }
                case 'removeRow': {
                    if (rods.length <= 1)
                        break;
                    var oldRods = JSON.stringify(rods);
                    var newRods_2 = rods.slice(0, -1);
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'rods',
                        old: oldRods,
                        new: JSON.stringify(newRods_2),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.id === id ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { rods: newRods_2 }) }) : _man;
                        });
                    });
                    announce(t('text.numberRackRowRemoved', { count: newRods_2.length }));
                    break;
                }
                case 'reverseBeads': {
                    if (rods.length < 6)
                        break;
                    var oldRods = JSON.stringify(rods);
                    var newRods_3 = rods.map(function (rod, idx) {
                        if (idx < 5)
                            return rod;
                        return __assign(__assign({}, rod), { data: __assign(__assign({}, rod.data), { isReversed: !rod.data.isReversed }) });
                    });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'rods',
                        old: oldRods,
                        new: JSON.stringify(newRods_3),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.id === id ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { rods: newRods_3 }) }) : _man;
                        });
                    });
                    // Maximum-clarity announcement: list the exact row numbers affected.
                    var bottomReversedStates = newRods_3.slice(5).map(function (r) { return r.data.isReversed; });
                    var affectedRowNumbers = bottomReversedStates.map(function (_, i) { return i + 6; });
                    // Build a natural-English row list:
                    //   1 row   → "6"          (used with singular template "Row {{n}} …")
                    //   2 rows  → "6 and 7"
                    //   3+ rows → "6, 7, and 8" (Oxford comma)
                    var isSingle = affectedRowNumbers.length === 1;
                    var rowList = '';
                    if (affectedRowNumbers.length === 2) {
                        rowList = t('text.rowListPair', {
                            a: affectedRowNumbers[0],
                            b: affectedRowNumbers[1],
                        });
                    }
                    else if (affectedRowNumbers.length >= 3) {
                        rowList = t('text.rowListMany', {
                            head: affectedRowNumbers.slice(0, -1).join(', '),
                            last: affectedRowNumbers[affectedRowNumbers.length - 1],
                        });
                    }
                    var allFalse = bottomReversedStates.every(function (v) { return v === false; });
                    var allTrue = bottomReversedStates.every(function (v) { return v === true; });
                    if (allFalse) {
                        announce(isSingle
                            ? t('text.numberRackSingleRowReversedRedFirst', { n: affectedRowNumbers[0] })
                            : t('text.numberRackRowsReversedRedFirst', { rows: rowList }));
                    }
                    else if (allTrue) {
                        announce(isSingle
                            ? t('text.numberRackSingleRowReversedWhiteFirst', { n: affectedRowNumbers[0] })
                            : t('text.numberRackRowsReversedWhiteFirst', { rows: rowList }));
                    }
                    else {
                        announce(isSingle
                            ? t('text.numberRackSingleRowToggled', { n: affectedRowNumbers[0] })
                            : t('text.numberRackRowsToggled', { rows: rowList }));
                    }
                    break;
                }
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        }
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused, scale, manipulative, id, rods]);
    var _h = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _h.startMultiDrag, moveMultiDrag = _h.moveMultiDrag, endMultiDrag = _h.endMultiDrag;
    // ─── Position Dragging (AnalogClock pattern) ───
    var onPositionDragStart = function (e) {
        var _a, _b;
        if ((lock === null || lock === void 0 ? void 0 : lock.positioning) || panningEnable) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        positionDragRef.current = {
            startPos: { x: x, y: y },
            offset: { x: center.x - mouseXInParent, y: center.y - mouseYInParent },
            hasMoved: false,
        };
        dragStartPositionRef.current = { x: center.x, y: center.y };
        window.addEventListener('mousemove', onPositionDragMove);
        window.addEventListener('touchmove', onPositionDragMove, { passive: false });
        window.addEventListener('mouseup', onPositionDragEnd);
        window.addEventListener('touchend', onPositionDragEnd);
        startMultiDrag();
    };
    var onPositionDragMove = useCallback(function (e) {
        var _a, _b;
        if (!positionDragRef.current)
            return;
        if ('touches' in e)
            e.preventDefault();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var _d = positionDragRef.current, startPos = _d.startPos, offset = _d.offset, hasMoved = _d.hasMoved;
        if (!hasMoved) {
            var dx = x - startPos.x;
            var dy = y - startPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                positionDragRef.current.hasMoved = true;
                setIsDragging(true);
            }
            else {
                return;
            }
        }
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        var newX = mouseXInParent + offset.x;
        var newY = mouseYInParent + offset.y;
        currentPositionRef.current = { x: newX, y: newY };
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
            });
        });
        if (dragStartPositionRef.current) {
            moveMultiDrag(newX - dragStartPositionRef.current.x, newY - dragStartPositionRef.current.y);
        }
    }, [id, zoomLevel, setManipulative, moveMultiDrag]);
    var onPositionDragEnd = useCallback(function (e) {
        if (!positionDragRef.current)
            return;
        if (!positionDragRef.current.hasMoved) {
        }
        else if (dragStartPositionRef.current && currentPositionRef.current) {
            var oldPosition = dragStartPositionRef.current;
            var newPosition = currentPositionRef.current;
            if (oldPosition.x !== newPosition.x || oldPosition.y !== newPosition.y) {
                var entry = {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(oldPosition),
                    new: JSON.stringify(newPosition),
                };
                if (!registerMultiSelectSaveEntries(id, [entry])) {
                    saveState(entry);
                }
                // Announce the move (same format as Spinner's manipulativeMoved).
                var dx = Math.round(newPosition.x - oldPosition.x);
                var dy = Math.round(newPosition.y - oldPosition.y);
                var directions = [];
                if (dy < 0)
                    directions.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                if (dy > 0)
                    directions.push("".concat(t('text.down'), " ").concat(dy));
                if (dx < 0)
                    directions.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                if (dx > 0)
                    directions.push("".concat(t('text.right'), " ").concat(dx));
                if (directions.length > 0) {
                    announce(t('text.manipulativeMoved', {
                        name: t('text.numberRack'),
                        direction: directions.join(" ".concat(t('text.and'), " ")),
                        x: Math.round(newPosition.x),
                        y: Math.round(newPosition.y),
                    }));
                }
            }
        }
        setIsDragging(false);
        positionDragRef.current.hasMoved = false;
        positionDragRef.current = null;
        dragStartPositionRef.current = null;
        currentPositionRef.current = null;
        window.removeEventListener('mousemove', onPositionDragMove);
        window.removeEventListener('touchmove', onPositionDragMove);
        window.removeEventListener('mouseup', onPositionDragEnd);
        window.removeEventListener('touchend', onPositionDragEnd);
        endMultiDrag();
    }, [id, selectedTool, setSelectedTool, onPositionDragMove, endMultiDrag]);
    // ─── Bead Dragging ───
    var onBeadMouseDown = function (e, rodIndex, beadIndex) {
        e.stopPropagation();
        e.preventDefault();
        if (panningEnable)
            return;
        var x = getClientPoint(e).x;
        beadDragRef.current = {
            rodIndex: rodIndex,
            beadIndex: beadIndex,
            startX: x,
            startBeadX: rods[rodIndex].data.beadsRawPosition[beadIndex],
            oldRods: JSON.parse(JSON.stringify(rods)),
        };
        // Snapshot which pairs are already touching so they don't trigger a pop
        var positions = rods[rodIndex].data.beadsRawPosition;
        var initialTouching = new Set();
        for (var i = 0; i < positions.length - 1; i++) {
            if (positions[i + 1] - positions[i] <= BEAD_SPACING + 0.5) {
                initialTouching.add(i);
            }
        }
        touchingPairsRef.current = initialTouching;
        window.addEventListener('mousemove', onBeadDragMove);
        window.addEventListener('touchmove', onBeadDragMove, { passive: false });
        window.addEventListener('mouseup', onBeadDragEnd);
        window.addEventListener('touchend', onBeadDragEnd);
    };
    var onBeadDragMove = useCallback(function (e) {
        var _a;
        if (!beadDragRef.current)
            return;
        if ('touches' in e)
            e.preventDefault();
        var x = getClientPoint(e).x;
        var _b = beadDragRef.current, rodIndex = _b.rodIndex, beadIndex = _b.beadIndex, startX = _b.startX, startBeadX = _b.startBeadX;
        var parentRect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!parentRect)
            return;
        var scaleVal = scale !== null && scale !== void 0 ? scale : 1;
        var deltaX = (x - startX) / (zoomLevel * scaleVal);
        var newBeadX = startBeadX + deltaX;
        var minX = ROD_LEFT_PADDING;
        var maxX = ROD_WIDTH - BEAD_DIAMETER - ROD_LEFT_PADDING;
        // Compute clamped position to check if bead actually moved
        var minForClamped = minX + beadIndex * BEAD_SPACING;
        var maxForClamped = maxX -
            (rodsRef.current[rodIndex].data.beadsRawPosition.length - beadIndex - 1) * BEAD_SPACING;
        var clampedX = Math.max(minForClamped, Math.min(maxForClamped, newBeadX));
        // Only play drag sound when bead position actually changes
        var beadMoved = lastBeadXRef.current === null || Math.abs(clampedX - lastBeadXRef.current) > 0.5;
        lastBeadXRef.current = clampedX;
        if (scrapingTimeoutRef.current) {
            clearTimeout(scrapingTimeoutRef.current);
        }
        if (beadMoved) {
            if (!scrapingRef.current) {
                playAudio('beadsDrag', { loop: CONTINUOUS_DRAG_SOUND, volume: 0.5 });
                scrapingRef.current = true;
            }
            if (CONTINUOUS_DRAG_SOUND) {
                scrapingTimeoutRef.current = window.setTimeout(function () {
                    stopAudio('beadsDrag');
                    scrapingRef.current = false;
                }, 150);
            }
        }
        else if (scrapingRef.current && CONTINUOUS_DRAG_SOUND) {
            stopAudio('beadsDrag');
            scrapingRef.current = false;
        }
        // Simulate new positions to detect all collisions in the chain
        var latestPositions = rodsRef.current[rodIndex].data.beadsRawPosition;
        var totalBeadsForSim = latestPositions.length;
        var simPositions = __spreadArray([], latestPositions, true);
        var minForDraggedSim = minX + beadIndex * BEAD_SPACING;
        var maxForDraggedSim = maxX - (totalBeadsForSim - beadIndex - 1) * BEAD_SPACING;
        simPositions[beadIndex] = Math.max(minForDraggedSim, Math.min(maxForDraggedSim, newBeadX));
        for (var i = beadIndex - 1; i >= 0; i--) {
            var max = simPositions[i + 1] - BEAD_SPACING;
            if (simPositions[i] > max)
                simPositions[i] = max;
        }
        for (var i = beadIndex + 1; i < totalBeadsForSim; i++) {
            var min = simPositions[i - 1] + BEAD_SPACING;
            if (simPositions[i] < min)
                simPositions[i] = min;
        }
        // Check which adjacent pairs are now touching
        var newTouching = new Set();
        for (var i = 0; i < totalBeadsForSim - 1; i++) {
            if (simPositions[i + 1] - simPositions[i] <= BEAD_SPACING + 0.5) {
                newTouching.add(i);
            }
        }
        // Play pop for each pair that just made contact
        newTouching.forEach(function (pair) {
            if (!touchingPairsRef.current.has(pair)) {
                playAudio('beadsCollision', {
                    volume: 0.5,
                });
            }
        });
        touchingPairsRef.current = newTouching;
        // Read positions from latest state via prev to avoid stale closure
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id !== id)
                    return item;
                var currentRods = item.data.rods;
                var totalBeads = currentRods[rodIndex].data.beadsRawPosition.length;
                // Clamp so there's always room for all beads on each side
                var minForDragged = minX + beadIndex * BEAD_SPACING;
                var maxForDragged = maxX - (totalBeads - beadIndex - 1) * BEAD_SPACING;
                var clampedBeadX = Math.max(minForDragged, Math.min(maxForDragged, newBeadX));
                // Get current positions from the LATEST state (not stale rods)
                var currentPositions = __spreadArray([], currentRods[rodIndex].data.beadsRawPosition, true);
                currentPositions[beadIndex] = clampedBeadX;
                // Push beads left (beads to the left of the dragged bead)
                for (var i = beadIndex - 1; i >= 0; i--) {
                    var maxAllowed = currentPositions[i + 1] - BEAD_SPACING;
                    if (currentPositions[i] > maxAllowed) {
                        currentPositions[i] = maxAllowed;
                    }
                }
                // Push beads right (beads to the right of the dragged bead)
                for (var i = beadIndex + 1; i < totalBeads; i++) {
                    var minAllowed = currentPositions[i - 1] + BEAD_SPACING;
                    if (currentPositions[i] < minAllowed) {
                        currentPositions[i] = minAllowed;
                    }
                }
                var updatedRods = __spreadArray([], currentRods, true);
                updatedRods[rodIndex] = __assign(__assign({}, updatedRods[rodIndex]), { data: __assign(__assign({}, updatedRods[rodIndex].data), { beadsRawPosition: currentPositions }) });
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rods: updatedRods }) });
            });
        });
    }, [id, scale, zoomLevel, setManipulative, CONTINUOUS_DRAG_SOUND]);
    var onBeadDragEnd = useCallback(function () {
        var _a, _b;
        if (!beadDragRef.current)
            return;
        var _c = beadDragRef.current, oldRods = _c.oldRods, rodIndex = _c.rodIndex;
        // Save state for undo using ref for latest rods
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'rods',
            old: JSON.stringify(oldRods),
            new: JSON.stringify(rodsRef.current),
        });
        // Detailed announcement: diff old vs new rod positions
        var rod = rodsRef.current[rodIndex];
        var oldRod = oldRods[rodIndex];
        if (rod && oldRod) {
            var msg = buildBeadMoveAnnouncement(oldRod.data.beadsRawPosition, rod.data.beadsRawPosition, rod.data.beads, rod.data.isReversed);
            // If the dragged bead ended at its min/max clamp, append an edge hint
            // so blind users know they've reached the counting / resting end.
            var draggedIdx = (_b = (_a = beadDragRef.current) === null || _a === void 0 ? void 0 : _a.beadIndex) !== null && _b !== void 0 ? _b : -1;
            var draggedPos = rod.data.beadsRawPosition[draggedIdx];
            var totalBeads = rod.data.beadsRawPosition.length;
            var minForBead = ROD_LEFT_PADDING + draggedIdx * BEAD_SPACING;
            var maxForBead = ROD_WIDTH - BEAD_DIAMETER - ROD_LEFT_PADDING - (totalBeads - draggedIdx - 1) * BEAD_SPACING;
            var edgeSuffix = '';
            if (draggedIdx >= 0) {
                if (Math.abs(draggedPos - minForBead) < 0.5) {
                    edgeSuffix = " ".concat(t('text.numberRackReachedLeftEdge', {
                        row: rodIndex + 1,
                        bead: draggedIdx + 1,
                    }));
                }
                else if (Math.abs(draggedPos - maxForBead) < 0.5) {
                    edgeSuffix = " ".concat(t('text.numberRackReachedRightEdge', {
                        row: rodIndex + 1,
                        bead: draggedIdx + 1,
                    }));
                }
            }
            var full = "".concat(msg).concat(edgeSuffix).trim();
            if (full)
                announce(full);
        }
        // Stop scraping sound and reset collision tracking
        if (scrapingTimeoutRef.current) {
            clearTimeout(scrapingTimeoutRef.current);
            scrapingTimeoutRef.current = null;
        }
        stopAudio('beadsDrag');
        scrapingRef.current = false;
        lastBeadXRef.current = null;
        touchingPairsRef.current = new Set();
        beadDragRef.current = null;
        window.removeEventListener('mousemove', onBeadDragMove);
        window.removeEventListener('touchmove', onBeadDragMove);
        window.removeEventListener('mouseup', onBeadDragEnd);
        window.removeEventListener('touchend', onBeadDragEnd);
    }, [id, onBeadDragMove]);
    // ─── Bead Image ───
    var getBeadImgSrc = function (beadValue, isReversed) {
        var isRed = isReversed ? beadValue === 1 : beadValue === 0;
        return isRed ? btnConfig.redBead.imgSrc : btnConfig.whiteBead.imgSrc;
    };
    // ─── Keyboard bead navigation (Phase 2) ───
    var focusBead = useCallback(function (rodIdx, beadIdx) {
        setFocusedBead({ rod: rodIdx, bead: beadIdx });
        requestAnimationFrame(function () {
            var el = beadRefs.current.get("".concat(rodIdx, "-").concat(beadIdx));
            if (el)
                el.focus();
        });
    }, []);
    // Listen for "Select Row" menu action → focus first bead of the chosen row.
    // The context menu's closeMenu re-focuses the rack wrapper on a setTimeout(0),
    // so we wait longer and retry to guarantee we win the focus race.
    useEffect(function () {
        var handler = function (data) {
            if (data.rackId !== id)
                return;
            var tryFocus = function (attempt) {
                if (attempt === void 0) { attempt = 0; }
                var el = beadRefs.current.get("".concat(data.rodIndex, "-0"));
                if (el) {
                    setSelectedRow(data.rodIndex);
                    setFocusedBead({ rod: data.rodIndex, bead: 0 });
                    el.focus();
                    // Backup announcement — iPad VoiceOver doesn't always follow
                    // programmatic .focus() after an aria-modal menu closes.
                    announce(t('text.numberRackRowSelected', { row: data.rodIndex + 1 }));
                }
                else if (attempt < 5) {
                    setTimeout(function () { return tryFocus(attempt + 1); }, 50);
                }
            };
            setTimeout(function () { return tryFocus(); }, 150);
        };
        customEventHandler.addEventListener('numberRackSelectRow', handler);
        return function () { return customEventHandler.removeEventListener('numberRackSelectRow', handler); };
    }, [id]);
    // Listen for bead context-menu actions (Shift Left / Shift Right) dispatched
    // from ContextMenu. Uses the same nudgeBead helper as arrow keys. The menu
    // stays open while the user nudges, so we don't steal focus back to the bead.
    useEffect(function () {
        var handler = function (data) {
            if (data.rackId !== id)
                return;
            // Shift Left / Shift Right move the bead by 2 bead-widths so keyboard /
            // VO users can reach a target faster than tapping arrow keys (which
            // nudge by 1 bead). nudgeBead's clamp still respects neighbor
            // collisions — if only 1 bead of room exists, the bead lands at the
            // clamp instead of overshooting.
            var delta = data.action === 'beadShiftLeft' ? -BEAD_SPACING * 2 : BEAD_SPACING * 2;
            nudgeBead(data.rodIndex, data.beadIndex, delta);
        };
        customEventHandler.addEventListener('numberRackBeadAction', handler);
        return function () { return customEventHandler.removeEventListener('numberRackBeadAction', handler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    // Toggle a bead between its default position and the active (left) side.
    // No longer used — Enter/Space now opens the bead context menu instead.
    // Kept here commented out in case we need to restore the toggle behavior later.
    // const toggleBead = useCallback(
    //   (rodIdx: number, beadIdx: number) => {
    //     const rod = rodsRef.current[rodIdx];
    //     if (!rod) return;
    //     const positions = [...rod.data.beadsRawPosition];
    //     const defaultPos = DEFAULT_BEAD_POSITIONS[beadIdx] ?? positions[beadIdx];
    //     const isAtDefault = Math.abs(positions[beadIdx] - defaultPos) < 1;
    //
    //     if (isAtDefault) {
    //       positions[beadIdx] = ROD_LEFT_PADDING;
    //     } else {
    //       positions[beadIdx] = defaultPos;
    //     }
    //
    //     for (let i = beadIdx - 1; i >= 0; i--) {
    //       const maxAllowed = positions[i + 1] - BEAD_SPACING;
    //       if (positions[i] > maxAllowed) positions[i] = maxAllowed;
    //     }
    //     for (let i = beadIdx + 1; i < positions.length; i++) {
    //       const minAllowed = positions[i - 1] + BEAD_SPACING;
    //       if (positions[i] < minAllowed) positions[i] = minAllowed;
    //     }
    //
    //     const oldRods = rodsRef.current;
    //     const newRods = oldRods.map((r, idx) =>
    //       idx === rodIdx
    //         ? { ...r, data: { ...r.data, beadsRawPosition: positions } }
    //         : r,
    //     );
    //
    //     saveState({
    //       type: id,
    //       id: id.split('_')[1],
    //       propertyName: 'rods',
    //       old: JSON.stringify(oldRods),
    //       new: JSON.stringify(newRods),
    //     });
    //
    //     setManipulative(prev =>
    //       prev.map(_man =>
    //         _man.data.id === id ? { ..._man, data: { ..._man.data, rods: newRods } } : _man,
    //       ),
    //     );
    //
    //     const movedCount = positions.filter(
    //       (p, i) => Math.round(p) !== Math.round(DEFAULT_BEAD_POSITIONS[i] ?? p),
    //     ).length;
    //     announce(t('text.numberRackRowCount', { row: rodIdx + 1, count: movedCount }));
    //   },
    //   [id, setManipulative],
    // );
    // Nudge a bead by `delta` pixels using the exact same clamp + cascade logic
    // as mouse drag. One call per arrow-key press. No merging — each press is a
    // separate undo entry.
    var nudgeBead = useCallback(function (rodIdx, beadIdx, delta) {
        var rod = rodsRef.current[rodIdx];
        if (!rod)
            return;
        var oldPositions = __spreadArray([], rod.data.beadsRawPosition, true);
        var positions = __spreadArray([], rod.data.beadsRawPosition, true);
        var totalBeads = positions.length;
        // Same clamp bounds as onBeadDragMove (§5 — accounts for all other beads).
        var minX = ROD_LEFT_PADDING;
        var maxX = ROD_WIDTH - BEAD_DIAMETER - ROD_LEFT_PADDING;
        var minForBead = minX + beadIdx * BEAD_SPACING;
        var maxForBead = maxX - (totalBeads - beadIdx - 1) * BEAD_SPACING;
        var newX = Math.max(minForBead, Math.min(maxForBead, positions[beadIdx] + delta));
        // No-op if clamp keeps us at the same pixel (can't move further in this direction).
        if (Math.abs(newX - positions[beadIdx]) < 0.5) {
            // No collision sound here — the bead never actually moved, so playing
            // a pop on a stationary bead feels wrong. The assertive announcement
            // below still tells SR users why the key press did nothing.
            var atLeft = delta < 0;
            // Use assertive so the failure feedback interrupts any pending polite
            // announcement and is heard immediately after the failed key press.
            announceAssertive(t(atLeft ? 'text.numberRackCannotMoveLeft' : 'text.numberRackCannotMoveRight', {
                row: rodIdx + 1,
                bead: beadIdx + 1,
            }));
            return;
        }
        positions[beadIdx] = newX;
        // Same push-left / push-right cascade as onBeadDragMove.
        for (var i = beadIdx - 1; i >= 0; i--) {
            var maxAllowed = positions[i + 1] - BEAD_SPACING;
            if (positions[i] > maxAllowed)
                positions[i] = maxAllowed;
        }
        for (var i = beadIdx + 1; i < totalBeads; i++) {
            var minAllowed = positions[i - 1] + BEAD_SPACING;
            if (positions[i] < minAllowed)
                positions[i] = minAllowed;
        }
        // Detect adjacent pairs that became touching as a result of this nudge.
        // Mirrors the same comparison used in mouse-drag's collision detection,
        // but local to this single keyboard step (no ref needed).
        var isPairTouching = function (pos, i) {
            return pos[i + 1] - pos[i] <= BEAD_SPACING + 0.5;
        };
        var newCollisions = 0;
        for (var i = 0; i < positions.length - 1; i++) {
            if (isPairTouching(positions, i) && !isPairTouching(oldPositions, i)) {
                newCollisions++;
            }
        }
        // Collision wins over drag — same call site sequence on a single frame
        // would otherwise layer two effects muddily. Collision is the more
        // informative feedback ("you bumped something").
        if (newCollisions > 0) {
            playAudio('beadsCollision', {
                volume: 0.5,
            });
        }
        else {
            // Slide audio length scales with how far the bead actually moved
            // (post-clamp). 1 bead = ~120ms, 2 beads = ~240ms, etc. — matches
            // the visible step instead of always playing the full audio file.
            var movedPx = Math.abs(positions[beadIdx] - oldPositions[beadIdx]);
            var beadsMoved = Math.max(1, Math.round(movedPx / BEAD_SPACING));
            var MS_PER_BEAD = 120;
            // Clear any prior in-flight stop so rapid arrow presses don't truncate
            // a longer subsequent slide.
            if (nudgeAudioStopTimerRef.current) {
                clearTimeout(nudgeAudioStopTimerRef.current);
            }
            playAudio('beadsDrag', { loop: true, volume: 0.5 });
            nudgeAudioStopTimerRef.current = window.setTimeout(function () {
                stopAudio('beadsDrag');
                nudgeAudioStopTimerRef.current = null;
            }, beadsMoved * MS_PER_BEAD);
        }
        var oldRods = rodsRef.current;
        var newRods = oldRods.map(function (r, idx) {
            return idx === rodIdx ? __assign(__assign({}, r), { data: __assign(__assign({}, r.data), { beadsRawPosition: positions }) }) : r;
        });
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'rods',
            old: JSON.stringify(oldRods),
            new: JSON.stringify(newRods),
        });
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                return _man.data.id === id ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { rods: newRods }) }) : _man;
            });
        });
        var msg = buildBeadMoveAnnouncement(oldPositions, positions, rod.data.beads, rod.data.isReversed);
        // If this key press was the one that reached the edge, tell the user
        // in the same breath so they don't have to press again to find out.
        var edgeSuffix = '';
        if (Math.abs(positions[beadIdx] - minForBead) < 0.5) {
            edgeSuffix = " ".concat(t('text.numberRackCannotMoveLeft', {
                row: rodIdx + 1,
                bead: beadIdx + 1,
            }));
        }
        else if (Math.abs(positions[beadIdx] - maxForBead) < 0.5) {
            edgeSuffix = " ".concat(t('text.numberRackCannotMoveRight', {
                row: rodIdx + 1,
                bead: beadIdx + 1,
            }));
        }
        var full = "".concat(msg).concat(edgeSuffix).trim();
        if (full)
            announce(full);
    }, [id, setManipulative]);
    var onBeadKeyDown = useCallback(function (e, rodIdx, beadIdx) {
        var _a, _b;
        var beadCount = (_b = (_a = rodsRef.current[rodIdx]) === null || _a === void 0 ? void 0 : _a.data.beads.length) !== null && _b !== void 0 ? _b : 0;
        switch (e.key) {
            case 'Tab': {
                e.preventDefault();
                e.stopPropagation();
                var next = e.shiftKey
                    ? (beadIdx - 1 + beadCount) % beadCount
                    : (beadIdx + 1) % beadCount;
                focusBead(rodIdx, next);
                break;
            }
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                nudgeBead(rodIdx, beadIdx, -BEAD_SPACING);
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                nudgeBead(rodIdx, beadIdx, BEAD_SPACING);
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setSelectedRow(null);
                setFocusedBead(null);
                // Wait for React to flush the tabIndex updates before moving focus
                // to the wrapper; otherwise the bead may briefly reclaim focus.
                requestAnimationFrame(function () {
                    var _a;
                    (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    // Some screen readers don't re-announce on programmatic focus,
                    // so explicitly announce that we're back on the rack wrapper.
                    announce(t('text.numberRackExitedBeadMode', { rows: rodsRef.current.length }));
                });
                break;
            case 'Enter':
            case ' ': {
                e.preventDefault();
                e.stopPropagation();
                var beadEl = beadRefs.current.get("".concat(rodIdx, "-").concat(beadIdx));
                if (beadEl) {
                    var rect = beadEl.getBoundingClientRect();
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: "".concat(id, "_bead_").concat(rodIdx, "_").concat(beadIdx),
                    });
                }
                break;
            }
        }
    }, [focusBead, nudgeBead, id, setContextMenu]);
    // ─── Render ───
    var computedHeight = rods.length * BEAD_DIAMETER + (rods.length + 1) * ROW_GAP;
    return (_jsx("div", { className: "numberRackWrapper ".concat(id), ref: wrapperRef, "data-manipulative-id": id, "data-MFDeselect": "true", role: "group", "aria-roledescription": t('text.numberRack'), "aria-label": (_c = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _c !== void 0 ? _c : t('text.numberRackLabel', { rows: rods.length }), tabIndex: 0, onFocus: function (e) {
            var _a, _b;
            try {
                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
            }
            catch (_c) {
                lastFocusWasScreenReader.current = false;
            }
        }, onBlur: function () {
            lastFocusWasScreenReader.current = false;
        }, onClick: function (e) {
            var _a;
            if (panningEnable)
                return;
            // iPad VoiceOver: double-tap fires a synthetic click after VO sets :focus-visible.
            // Real touch / mouse clicks don't set :focus-visible, so they skip this branch.
            if (isTouchPrimary && lastFocusWasScreenReader.current) {
                e.preventDefault();
                lastFocusWasScreenReader.current = false;
                if (!selectedTool.includes(id)) {
                    setSelectedTool([id]);
                }
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect) {
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: id,
                    });
                }
            }
        }, onKeyDown: function (e) {
            var _a;
            if (panningEnable)
                return;
            // Arrow keys → enter bead navigation mode
            if (e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight' ||
                e.key === 'ArrowUp' ||
                e.key === 'ArrowDown') {
                e.preventDefault();
                // Resume from last focused bead, otherwise start at (0, 0)
                var start = focusedBead !== null && focusedBead !== void 0 ? focusedBead : { rod: 0, bead: 0 };
                focusBead(start.rod, start.bead);
                return;
            }
            if (e.key === 'ContextMenu' ||
                (e.shiftKey && e.key === 'F10') ||
                e.key === 'Enter' ||
                e.key === ' ') {
                e.preventDefault();
                if (!selectedTool.includes(id)) {
                    setSelectedTool([id]);
                }
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect) {
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: id,
                    });
                }
            }
        }, style: {
            transform: "translate(-50%, -50%) scale(".concat(scale !== null && scale !== void 0 ? scale : 1, ")"),
            width: "".concat(dimensions.width, "px"),
            height: "".concat(computedHeight, "px"),
            left: "".concat(Math.round(center.x), "px"),
            top: "".concat(Math.round(center.y), "px"),
            // padding: '0.625rem',
            // border: `0.125rem solid ${isFocused ? 'black' : 'transparent'}`,
            boxShadow: "0 0 20px ".concat(isFocused ? '#5063AE' : 'transparent'),
        }, children: _jsxs("div", { className: "numberRackFrame", children: [_jsx(ButtonComponent, { defaultCSS: "numberRackDragOverlay ".concat(isDragging ? 'movingObject' : ''), avoidDefaultStyle: true, onMouseDown: onPositionDragStart, onTouchStart: onPositionDragStart, clickHandler: function (e) {
                        setSelectedTool(selectTool(e, id, selectedTool, false));
                    }, ariaLabel: t('text.moveNumberRack') }), _jsx("div", { className: "numberRackEndCap left", "aria-hidden": true }), _jsx("div", { className: "numberRackRodsContainer", role: "list", "aria-label": t('text.rows'), children: rods.map(function (rod, rodIndex) { return (_jsxs("div", { className: "numberRackRod", role: "listitem", "aria-label": t('text.rowLabel', { index: rodIndex + 1 }), children: [_jsx("div", { className: "numberRackRodLeftSideLine", "aria-hidden": true }), _jsx("div", { className: "numberRackRodLine", "aria-hidden": true }), rod.data.beads.map(function (beadValue, beadIndex) { return (_jsx("div", { className: "numberRackBead", "data-manipulative-id": "".concat(id, "_bead_").concat(rodIndex, "_").concat(beadIndex), style: {
                                    left: "".concat(rod.data.beadsRawPosition[beadIndex], "px"),
                                }, children: _jsx(ButtonComponent, { type: "imageType4", imgSrc: getBeadImgSrc(beadValue, rod.data.isReversed), defaultCSS: "numberRackBeadBtn", normalCSS: "numberRackBeadBtnNormal", noAudio: true, avoidOnClickHoverReset: true, avoidDefaultStyle: true, onMouseDown: function (e) { return onBeadMouseDown(e, rodIndex, beadIndex); }, onTouchStart: function (e) { return onBeadMouseDown(e, rodIndex, beadIndex); }, onKeyDown: function (e) { return onBeadKeyDown(e, rodIndex, beadIndex); }, clickHandler: function (e) {
                                        // iPad VoiceOver double-tap fires a synthetic click (detail === 0).
                                        // Real mouse/touch clicks have detail >= 1 and are ignored here
                                        // so the drag path is untouched.
                                        var detail = e.detail;
                                        if (!isTouchPrimary || detail !== 0)
                                            return;
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var beadEl = beadRefs.current.get("".concat(rodIndex, "-").concat(beadIndex));
                                        if (beadEl) {
                                            var rect = beadEl.getBoundingClientRect();
                                            setContextMenu({
                                                x: rect.right,
                                                y: rect.top,
                                                manipulativeId: "".concat(id, "_bead_").concat(rodIndex, "_").concat(beadIndex),
                                            });
                                        }
                                    }, tabIndex: selectedRow !== null
                                        ? rodIndex === selectedRow
                                            ? 0
                                            : -1
                                        : (focusedBead === null || focusedBead === void 0 ? void 0 : focusedBead.rod) === rodIndex && (focusedBead === null || focusedBead === void 0 ? void 0 : focusedBead.bead) === beadIndex
                                            ? 0
                                            : -1, reference: function (el) {
                                        beadRefs.current.set("".concat(rodIndex, "-").concat(beadIndex), el);
                                    }, ariaLabel: t('text.beadLabel', {
                                        row: rodIndex + 1,
                                        color: rod.data.isReversed
                                            ? beadValue === 1
                                                ? t('text.red')
                                                : t('text.white')
                                            : beadValue === 0
                                                ? t('text.red')
                                                : t('text.white'),
                                        bead: beadIndex + 1,
                                    }), disabled: panningEnable }) }, "".concat(rod.id, "_bead_").concat(beadIndex))); }), _jsx("div", { className: "numberRackRodRightSideLine", "aria-hidden": true })] }, rod.id)); }) }), _jsx("div", { className: "numberRackEndCap right", "aria-hidden": true })] }) }));
};
export default NumberRackManipulative;
